// «Actualizar desde Zentral»: abre la vista previa de una entidad. Solo con `retail.integration.reconcile` y cadena integrada.
import { useRef, useState } from 'react';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ReconciliationEntityEnum } from '@fiado/type-kit/bin/retailIntegration/enums/ReconciliationEntityEnum';
import { Button, usePermissions } from '@/kit';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useRetailerIntegration } from '../../integration/api/useRetailerIntegration';
import { RETAIL_RECONCILE_PERMISSION } from '../lib/reconciliation';
import type { ReconcileTarget } from '../api/useReconciliation';
import { ReconcilePreviewDialog, type StoreOption } from './ReconcilePreviewDialog';

const BUTTON_CLASS = 'h-6 shrink-0 px-2 text-[11px]';

/** Sin el permiso no se pinta ni se lee la cadena. */
function useCanReconcile(): boolean {
    return usePermissions().hasPermission(RETAIL_RECONCILE_PERMISSION);
}

interface ReconcileButtonProps {
    retailerId: string | null;
    target: ReconcileTarget;
    /** Se llama cuando la corrida real termina COMPLETE, para refrescar la pantalla. */
    onComplete?: () => void;
}

export function ReconcileButton(props: ReconcileButtonProps) {
    return useCanReconcile() ? <IntegratedReconcileButton {...props} /> : null;
}

function IntegratedReconcileButton({ retailerId, target, onComplete }: ReconcileButtonProps) {
    const { t } = useTranslation('reconciliation');
    const { integrated, mode } = useRetailerIntegration(retailerId);
    const [open, setOpen] = useState(false);
    if (!integrated || retailerId === null) return null;
    return (
        <>
            <Button
                type="button"
                size="sm"
                variant="outline"
                className={BUTTON_CLASS}
                leftIcon={<RefreshCw className="size-3" aria-hidden />}
                onClick={() => {
                    setOpen(true);
                }}
            >
                {t('button')}
            </Button>
            <ReconcilePreviewDialog
                isOpen={open}
                retailerId={retailerId}
                target={target}
                mode={mode}
                onClose={() => {
                    setOpen(false);
                }}
                onApplied={onComplete}
            />
        </>
    );
}

type MenuChoice = 'catalog' | 'inventoryAll' | 'inventoryStore';

const MENU_TARGETS: Record<MenuChoice, ReconcileTarget> = {
    catalog: { entity: ReconciliationEntityEnum.CATALOG },
    inventoryAll: { entity: ReconciliationEntityEnum.INVENTORY },
    inventoryStore: { entity: ReconciliationEntityEnum.INVENTORY },
};

const MENU_ORDER: readonly MenuChoice[] = ['catalog', 'inventoryAll', 'inventoryStore'];

interface ReconcileMenuProps {
    retailerId: string | null;
    /** Tiendas de la cadena para «Inventario de una tienda…». */
    stores: readonly StoreOption[];
    onComplete?: () => void;
}

/** Inventario: el mismo botón con menú de catálogo, inventario completo o de una tienda. */
export function ReconcileMenu(props: ReconcileMenuProps) {
    return useCanReconcile() ? <IntegratedReconcileMenu {...props} /> : null;
}

function IntegratedReconcileMenu({ retailerId, stores, onComplete }: ReconcileMenuProps) {
    const { t } = useTranslation('reconciliation');
    const { integrated, mode } = useRetailerIntegration(retailerId);
    const [choice, setChoice] = useState<MenuChoice | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    if (!integrated || retailerId === null) return null;
    return (
        <>
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button
                        ref={triggerRef}
                        type="button"
                        size="sm"
                        variant="outline"
                        className={BUTTON_CLASS}
                        leftIcon={<RefreshCw className="size-3" aria-hidden />}
                        rightIcon={<ChevronDown className="size-3" aria-hidden />}
                    >
                        {t('button')}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                    {MENU_ORDER.map((key) => (
                        <DropdownMenuItem
                            key={key}
                            className="flex flex-col items-start gap-0.5"
                            onSelect={() => {
                                setChoice(key);
                            }}
                        >
                            <span className="font-medium">{t(`menu.${key}`)}</span>
                            <span className="text-[11px] text-muted-foreground">
                                {t(`menu.${key}Hint`)}
                            </span>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            <ReconcilePreviewDialog
                isOpen={choice !== null}
                retailerId={retailerId}
                target={MENU_TARGETS[choice ?? 'catalog']}
                mode={mode}
                chooseStore={choice === 'inventoryStore'}
                stores={stores}
                onClose={() => {
                    setChoice(null);
                    // El modal se abrió desde un elemento del menú que ya no existe: el foco va al botón.
                    setTimeout(() => {
                        triggerRef.current?.focus();
                    }, 0);
                }}
                onApplied={onComplete}
            />
        </>
    );
}
